(function(root, factory) {
  var api = factory();
  root.HDCartDiscountRuntime = api;
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }
})(typeof window !== "undefined" ? window : globalThis, function() {
  function parseInteger(value) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  function normalizeText(value) {
    return String(value == null ? "" : value).toLowerCase();
  }

  function createRuntimeState() {
    return {
      initialized: false,
      inFlight: false,
      activeTrigger: null,
      queuedTrigger: null,
      lastTrigger: null,
      lastTriggerAt: 0,
    };
  }

  function ensureRuntimeState(root) {
    var target = root && typeof root === "object" ? root : {};
    if (!target.__HDCartDiscountRuntimeState) {
      target.__HDCartDiscountRuntimeState = createRuntimeState();
    }
    return target.__HDCartDiscountRuntimeState;
  }

  function claimInitialization(state) {
    var runtimeState = state || createRuntimeState();
    if (runtimeState.initialized) return false;
    runtimeState.initialized = true;
    return true;
  }

  function toTimestamp(value) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? Date.now() : parsed;
  }

  function beginDiscountCheck(state, trigger, now, duplicateWindowMs) {
    var runtimeState = state || createRuntimeState();
    var nextTrigger = String(trigger || "unknown");
    var nextNow = toTimestamp(now);
    var duplicateWindow = Math.max(0, parseInt(duplicateWindowMs, 10) || 0);

    if (runtimeState.inFlight) {
      runtimeState.queuedTrigger = nextTrigger;
      return {
        action: "queued",
        trigger: nextTrigger,
        activeTrigger: runtimeState.activeTrigger || null,
        queuedTrigger: runtimeState.queuedTrigger || null,
      };
    }

    if (
      runtimeState.lastTrigger === nextTrigger &&
      duplicateWindow > 0 &&
      nextNow - runtimeState.lastTriggerAt < duplicateWindow
    ) {
      return {
        action: "skipped_duplicate",
        trigger: nextTrigger,
        activeTrigger: runtimeState.activeTrigger || null,
        queuedTrigger: runtimeState.queuedTrigger || null,
      };
    }

    runtimeState.inFlight = true;
    runtimeState.activeTrigger = nextTrigger;
    runtimeState.lastTrigger = nextTrigger;
    runtimeState.lastTriggerAt = nextNow;

    return {
      action: "started",
      trigger: nextTrigger,
      activeTrigger: runtimeState.activeTrigger,
      queuedTrigger: runtimeState.queuedTrigger || null,
    };
  }

  function finishDiscountCheck(state) {
    var runtimeState = state || createRuntimeState();
    var queuedTrigger = runtimeState.queuedTrigger || null;
    runtimeState.inFlight = false;
    runtimeState.activeTrigger = null;
    runtimeState.queuedTrigger = null;
    return queuedTrigger;
  }

  function shouldFallbackDiscountApiRequest(errorInput) {
    var error = errorInput && typeof errorInput === "object" ? errorInput : {};
    var httpStatus = parseInteger(error.http_status != null ? error.http_status : error.status);
    if (httpStatus > 0) return false;

    var readyState = parseInteger(error.ready_state != null ? error.ready_state : error.readyState);
    var statusText = normalizeText(error.status_text != null ? error.status_text : error.statusText);
    var errorHint = normalizeText(error.error_hint);
    var errorName = normalizeText(error.error_name || error.name);
    var errorMessage = normalizeText(error.error_message || error.message);

    return (
      errorHint === "request_blocked_or_network_unavailable" ||
      readyState === 0 ||
      statusText === "error" ||
      statusText === "timeout" ||
      statusText === "abort" ||
      (errorName === "typeerror" && errorMessage.indexOf("load failed") !== -1)
    );
  }

  function serializeDiscountApiRequest(data) {
    var entries = data && typeof data === "object" ? data : {};
    if (typeof URLSearchParams !== "undefined") {
      var searchParams = new URLSearchParams();
      Object.keys(entries).forEach(function(key) {
        if (entries[key] == null) return;
        searchParams.append(key, String(entries[key]));
      });
      return searchParams;
    }

    return Object.keys(entries)
      .filter(function(key) {
        return entries[key] != null;
      })
      .map(function(key) {
        return encodeURIComponent(key) + "=" + encodeURIComponent(String(entries[key]));
      })
      .join("&");
  }

  function parseDiscountApiResponse(responseText) {
    var text = String(responseText == null ? "" : responseText);
    if (!text) return "";

    try {
      return JSON.parse(text);
    } catch (error) {
      return text;
    }
  }

  async function requestDiscountApi(options) {
    var opts = options && typeof options === "object" ? options : {};
    var jqueryAjax = typeof opts.jqueryAjax === "function" ? opts.jqueryAjax : null;
    var fetchImpl = typeof opts.fetchImpl === "function" ? opts.fetchImpl : null;
    var url = String(opts.url || "");
    var data = opts.data && typeof opts.data === "object" ? opts.data : {};
    var timeoutMs = Math.max(0, parseInteger(opts.timeoutMs));

    if (!jqueryAjax) {
      throw new Error("missing_jquery_ajax");
    }

    try {
      return {
        response: await jqueryAjax({
          type: "POST",
          url: url,
          data: data,
          timeout: timeoutMs,
        }),
        transport: "jquery",
      };
    } catch (ajaxError) {
      if (!fetchImpl || !shouldFallbackDiscountApiRequest(ajaxError)) {
        throw ajaxError;
      }

      var abortController = null;
      var timeoutId = null;
      if (timeoutMs > 0 && typeof AbortController !== "undefined") {
        abortController = new AbortController();
        timeoutId = setTimeout(function() {
          abortController.abort();
        }, timeoutMs);
      }

      try {
        var response = await fetchImpl(url, {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: serializeDiscountApiRequest(data),
          signal: abortController ? abortController.signal : undefined,
        });

        if (!response || !response.ok) {
          var responseText = response && typeof response.text === "function"
            ? await response.text()
            : "";
          var httpError = new Error("discount_api_http_" + (response ? response.status : "unknown"));
          httpError.name = "DiscountApiHttpError";
          httpError.status = response ? response.status : 0;
          httpError.statusText = response ? response.statusText : "";
          httpError.responseText = responseText;
          httpError.ajax_error = ajaxError;
          throw httpError;
        }

        var text = typeof response.text === "function" ? await response.text() : "";
        return {
          response: parseDiscountApiResponse(text),
          transport: "fetch",
        };
      } catch (fetchError) {
        if (fetchError && typeof fetchError === "object" && !fetchError.ajax_error) {
          fetchError.ajax_error = ajaxError;
        }
        throw fetchError;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }
  }

  function resolveCodeSyncRecoveryAction(options) {
    var opts = options && typeof options === "object" ? options : {};
    var matchedExpectedCode = !!opts.matchedExpectedCode;
    var cartReadFailed = !!opts.cartReadFailed;
    var mismatchHasMismatch = !!opts.mismatchHasMismatch;
    var riskyDuplicateVariantCart = !!opts.riskyDuplicateVariantCart;
    var missingExpectedItemCount = parseInteger(opts.missingExpectedItemCount);
    var unexpectedObservedItemCount = parseInteger(opts.unexpectedObservedItemCount);
    var observedManagedCodeMatchesPreviousCode = !!opts.observedManagedCodeMatchesPreviousCode;

    if (matchedExpectedCode) {
      return {
        action: "confirmed",
        shouldFailClosed: false,
        shouldScheduleResync: false,
        shouldEmitMismatch: false,
      };
    }

    if (cartReadFailed) {
      return {
        action: "schedule_resync_unreadable_cart",
        shouldFailClosed: false,
        shouldScheduleResync: true,
        shouldEmitMismatch: false,
      };
    }

    if (
      mismatchHasMismatch &&
      riskyDuplicateVariantCart &&
      missingExpectedItemCount === 1 &&
      unexpectedObservedItemCount === 0 &&
      observedManagedCodeMatchesPreviousCode
    ) {
      return {
        action: "schedule_resync_risky_duplicate_variant_cart",
        shouldFailClosed: false,
        shouldScheduleResync: true,
        shouldEmitMismatch: true,
      };
    }

    if (mismatchHasMismatch) {
      return {
        action: "fail_closed",
        shouldFailClosed: true,
        shouldScheduleResync: false,
        shouldEmitMismatch: true,
      };
    }

    return {
      action: "schedule_resync",
      shouldFailClosed: false,
      shouldScheduleResync: true,
      shouldEmitMismatch: true,
    };
  }

  return {
    createRuntimeState: createRuntimeState,
    ensureRuntimeState: ensureRuntimeState,
    claimInitialization: claimInitialization,
    beginDiscountCheck: beginDiscountCheck,
    finishDiscountCheck: finishDiscountCheck,
    requestDiscountApi: requestDiscountApi,
    resolveCodeSyncRecoveryAction: resolveCodeSyncRecoveryAction,
  };
});
